// SPDX-License-Identifier: BUSL-1.1

/**
 * Surface drilldown sheet — opens when an operator clicks a status indicator
 * to ask "why am I seeing this?". This is the FIRST place brand vocabulary
 * appears in user-visible chrome (ADR 008 Phase 3A "earned drilldowns").
 *
 * Per ADR 009: vocabulary earns its way into the visible surface by being
 * needed for explanation, not by being added as decoration. The sheet
 * answers a question the operator deliberately asked — perfect place for
 * EVIDENCE CHAIN, EPISTEMIC DRIFT, INTENT BINDING, AUTHORIZED POSTURE
 * framing to land.
 *
 * Render-by-exception is enforced one level up: callers only mount this
 * sheet when the drilldown feature flag is on AND the operator clicked.
 *
 * Telemetry (per ADR 008 §"Phase 3A validation gate"): every open emits
 * a `[surface.drilldown.opened]` log line. Cloudflare logpush surfaces
 * this for the gating signal "operators open drilldowns at non-trivial
 * rates" before Phase 3B chrome flips.
 */

import { useEffect } from 'react';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import type { ProjectedSurface, SurfaceBrandTerm } from '@/types/api';

interface SurfaceDrilldownSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentId: string;
  surface: ProjectedSurface | null;
}

/**
 * Brand-vocabulary explanation strings. The drilldown's job is to teach
 * the term in context — what is EVIDENCE CHAIN, what does it mean that
 * it's PARTIAL right now? Each entry is a one-sentence operator-grade
 * gloss tied to the active surface state.
 *
 * Phase 3A intentionally keeps these terse. Phase 3B+ may evolve them as
 * we learn what operators actually ask in interviews.
 */
function brandTermGloss(term: SurfaceBrandTerm, severity: ProjectedSurface['severity']): string | null {
  if (term == null) return null;
  switch (term) {
    case 'EVIDENCE_CHAIN':
      return severity === 'healthy'
        ? 'EVIDENCE CHAIN: INTACT — every claim on this agent is backed by a replayable artifact.'
        : 'EVIDENCE CHAIN: PARTIAL — at least one claim cannot be linked to a replayable artifact.';
    case 'EPISTEMIC_DRIFT':
      return severity === 'healthy'
        ? 'EPISTEMIC DRIFT: NONE — the agent\'s reported state matches ground truth.'
        : 'EPISTEMIC DRIFT — measurable distance between reported state and ground truth. Drilldown shows which claims diverge.';
    case 'INTENT_BINDING':
      return severity === 'critical'
        ? 'INTENT BINDING: BREACHED — the agent has exceeded the bound budget envelope. Action required.'
        : 'INTENT BINDING — the agent\'s declared envelope (budget, capabilities, scope) constrains what it can do.';
    case 'AUTHORIZED_POSTURE':
      return 'AUTHORIZED POSTURE — the set of postures (STUDY/CONSTRUCT/INTERVENE) you can hold on this agent right now.';
    case 'POSTURE_TRANSITION':
      return 'POSTURE TRANSITION — the audited event of moving between postures. Always logged.';
  }
}

export function SurfaceDrilldownSheet({
  open,
  onOpenChange,
  agentId,
  surface,
}: SurfaceDrilldownSheetProps) {
  // Telemetry: emit on open. Phase 2 mirrors this on the server side via
  // dual-run-compare's [surfaces.emitted] line. The pair drives the
  // "emitted vs rendered" gating signal for Phase 3B.
  useEffect(() => {
    if (open && surface) {
      console.log('[surface.drilldown.opened]', {
        agentId,
        type: surface.type,
        severity: surface.severity,
        brandTerm: surface.brandTerm,
      });
    }
  }, [open, surface, agentId]);

  if (!surface) return null;

  const gloss = brandTermGloss(surface.brandTerm, surface.severity);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex flex-col gap-4 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{surface.label}</SheetTitle>
          <SheetDescription>{surface.valueLabel}</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-4 px-4 text-sm">
          <Section heading="State">
            <SeverityChip severity={surface.severity} />
          </Section>

          {gloss && (
            <Section heading="Why this matters">
              <p className="text-muted-foreground">{gloss}</p>
            </Section>
          )}

          <Section heading="Evidence">
            <a
              href={surface.evidenceUrl}
              className="break-all text-blue-400 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              {surface.evidenceUrl}
            </a>
            <p className="mt-1 text-xs text-muted-foreground">
              Drilldown link to the audit log row, JSONL trace, or intent binding that produced this state.
            </p>
          </Section>

          {surface.affordance && (
            <Section heading="Available action">
              <p className="text-muted-foreground">
                {surface.affordance.label}
                {surface.affordance.arming !== 'none' && (
                  <span className="ml-2 rounded bg-amber-900/40 px-1.5 py-0.5 text-xs text-amber-300">
                    requires {surface.affordance.arming}
                  </span>
                )}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Click-through is gated by ADR 006&apos;s posture engine — affordances render here only when authorized for the current user.
              </p>
            </Section>
          )}
        </div>

        <SheetFooter>
          <SheetClose
            render={
              <Button variant="ghost" className="self-start">
                Close
              </Button>
            }
          />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{heading}</p>
      {children}
    </div>
  );
}

function SeverityChip({ severity }: { severity: ProjectedSurface['severity'] }) {
  const className =
    severity === 'critical'
      ? 'bg-red-900/40 text-red-300'
      : severity === 'degraded'
        ? 'bg-amber-900/40 text-amber-300'
        : severity === 'watch'
          ? 'bg-amber-900/30 text-amber-200'
          : severity === 'healthy'
            ? 'bg-green-900/40 text-green-300'
            : 'bg-neutral-800 text-neutral-400';
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${className}`}>
      {severity.toUpperCase()}
    </span>
  );
}
